import { useEffect, useState } from 'react';
import {
  AtcRegimeDataMap,
  AtcRegimeMap,
  CALCULATOR_TAB_PROPS_ATC,
} from '../types';
import { generateIncomeLabel, getFinalMaxValue } from '../utils';
import { AtcTaxCalculation } from './atc-tax-calculation';

export const AtcGraphCalculation = ({
  taxCalculation,
  activeTab,
}: CALCULATOR_TAB_PROPS_ATC) => {
  const regime = AtcRegimeMap[activeTab || 0];
  const [targetHeight, setTargetHeight] = useState(220);

  useEffect(() => {
    if (window) {
      const viewPortWidth = window?.innerWidth;
      const updateHeight = () => {
        if (viewPortWidth < 768) {
          setTargetHeight(184);
        } else if (viewPortWidth >= 768 && viewPortWidth <= 1024) {
          setTargetHeight(160);
        } else {
          setTargetHeight(220);
        }
      };
      updateHeight();
    }
  }, []);

  const regimeData =
    regime && taxCalculation?.[regime as keyof AtcRegimeDataMap];
  if (!regimeData) {
    return <></>;
  }
  const { totalAdvanceTaxPaid } = taxCalculation;
  const { totalAdvanceTaxDue, currentPayable, netTaxLiability } = regimeData;
  const maxValue = Math.max(
    parseInt(totalAdvanceTaxDue),
    parseInt(currentPayable),
    parseInt(totalAdvanceTaxPaid),
    parseInt(netTaxLiability)
  );
  const roundedMaxValue = getFinalMaxValue(Number(maxValue));
  const steps = 4;
  const AtcIncomeLabels = generateIncomeLabel(steps, roundedMaxValue);

  const calculatePercetage = (
    base: number,
    value: number,
    graphBase: number
  ) => {
    const percentage = Math.floor((Number(value) / Number(base)) * 100);
    const graphHeight = Math.floor((graphBase * percentage) / 100);
    const defaultHeight = 4;
    return graphHeight ? graphHeight : defaultHeight;
  };

  return (
    <div className="flex justify-between sm:block gap-[60px] md:gap-5 md:justify-center">
      <div className="graphTabWrapper mt-6 w-[364px] min-w-[320px] lg:min-w-[345px] md:min-w-[270px] sm:w-fit sm:min-w-[300px] sm:mt-[58px]">
        <div className="bg-[url('https://assets.clear.in/cleartax/images/1724840642_group18142194442.png')] bg-no-repeat bg-bottom bg-contain flex justify-end gap-4 items-end mt-5 sm:mt-10 md:mt-5 h-[217px] md:h-[160px] sm:h-[185px] sm:ml-7 relative pr-10 sm:pr-0 max-w-[324px] sm:justify-center">
          <div
            className="animate-height w-11 sm:w-8 md:w-8 rounded-t bg-blue-100"
            title={'Advance tax due'}
            style={
              {
                '--target-height': `${calculatePercetage(
                  maxValue,
                  parseInt(totalAdvanceTaxDue),
                  targetHeight
                )}px`,
              } as React.CSSProperties
            }
          ></div>
          <div
            className="animate-height w-11 sm:w-8 md:w-7 rounded-t bg-blue-450"
            title={'Advanced tax payable'}
            style={
              {
                '--target-height': `${calculatePercetage(
                  maxValue,
                  parseInt(currentPayable),
                  targetHeight
                )}px`,
              } as React.CSSProperties
            }
          ></div>
          <div
            className="animate-height w-11 sm:w-8 md:w-7 rounded-t bg-green-150"
            title={'Advanced tax paid'}
            style={
              {
                '--target-height': `${calculatePercetage(
                  maxValue,
                  parseInt(totalAdvanceTaxPaid),
                  targetHeight
                )}px`,
              } as React.CSSProperties
            }
          ></div>
          <div
            className="animate-height w-11 sm:w-8 md:w-7 rounded-t bg-green-250"
            title={'Tax payable'}
            style={
              {
                '--target-height': `${calculatePercetage(
                  maxValue,
                  parseInt(netTaxLiability),
                  targetHeight
                )}px`,
              } as React.CSSProperties
            }
          ></div>
          <>
            <div className="absolute left-0 text-s-10 md:text-s-8 text-grey-700 bottom-[54px] sm:bottom-[46px] md:bottom-[40px]">
              ₹{AtcIncomeLabels[1]}
            </div>
            <div className="absolute left-0 text-s-10 md:text-s-8 text-grey-700 bottom-[108px] sm:bottom-[92px] md:bottom-[80px]">
              ₹{AtcIncomeLabels[2]}
            </div>
            <div className="absolute left-0 text-s-10 md:text-s-8 text-grey-700 bottom-[162px] sm:bottom-[138px] md:bottom-[120px]">
              ₹{AtcIncomeLabels[3]}
            </div>
            <div className="absolute left-0 text-s-10 md:text-s-8 text-grey-700 bottom-[215px] sm:bottom-[185px] md:bottom-[160px]">
              ₹{AtcIncomeLabels[4]}
            </div>
          </>
        </div>
        <div className="grid grid-cols-2 gap-4 mt-12 sm:mt-6 sm:gap-2 sm:gap-x-0">
          <div className="flex gap-2 items-center">
            <div className="w-[18px] h-[18px] rounded-sm bg-blue-100 "></div>
            <div className="text-s-14 font-medium sm:text-s-12">
              Advance tax due
            </div>
          </div>
          <div className="flex gap-2 lg:gap-[0.8px] items-center">
            <div className="w-[18px] h-[18px] rounded-sm bg-blue-450"></div>
            <div className="text-s-14 font-medium whitespace-nowrap sm:whitespace-nowrap sm:text-s-12 md:whitespace-normal">
              Advanced tax payable
            </div>
          </div>
          <div className="flex gap-2 items-center">
            <div className="w-[18px] h-[18px] rounded-sm bg-green-150"></div>
            <div className="text-s-14 font-medium sm:text-s-12">
              Advance Tax paid
            </div>
          </div>
          <div className="flex gap-2 items-center">
            <div className="w-[18px] h-[18px] rounded-sm bg-green-250"></div>
            <div className="text-s-14 font-medium sm:text-s-12">
              Tax payable
            </div>
          </div>
        </div>
        <div className="text-s-14 font-medium line leading-5 mt-6 text-font-200 sm:text-s-12">
          <span className="text-s-14 font-bold sm:text-s-12"> Note :</span>{' '}
          Above tax calculation is suggested based on the lowest tax liability
          between new regime and old regime
        </div>
      </div>
      <AtcTaxCalculation
        taxCalculation={taxCalculation}
        activeTab={activeTab}
      />
    </div>
  );
};
